import React from 'react';
import { Coins, RefreshCw, AlertTriangle } from 'lucide-react';
import { CreditService } from '../../services/credit.service';

interface CreditBalanceProps {
  credits: number | null;
  loading: boolean;
  error: string | null;
  onRefresh?: () => void;
  showRefreshButton?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export const CreditBalance: React.FC<CreditBalanceProps> = ({
  credits,
  loading,
  error,
  onRefresh,
  showRefreshButton = false,
  size = 'md',
  showIcon = true,
  className = '',
}) => {
  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRefresh) {
      onRefresh();
    }
  };

  const isLowCredit = credits !== null && CreditService.isLowCredit(credits);

  // Size configurations
  const sizeConfig = {
    sm: {
      text: 'text-sm',
      icon: 'w-4 h-4',
      button: 'w-6 h-6',
    },
    md: {
      text: 'text-base',
      icon: 'w-5 h-5',
      button: 'w-8 h-8',
    },
    lg: {
      text: 'text-lg',
      icon: 'w-6 h-6',
      button: 'w-10 h-10',
    },
  };

  const config = sizeConfig[size];

  if (error) {
    return (
      <div className={`flex items-center space-x-2 text-red-600 ${config.text} ${className}`}>
        <AlertTriangle className={config.icon} />
        <span>Error loading credits</span>
        {showRefreshButton && onRefresh && (
          <button
            onClick={handleRefresh}
            className="p-1 hover:bg-red-50 rounded transition-colors"
            title="Refresh credits"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  if (loading && credits === null) {
    return (
      <div className={`flex items-center space-x-2 text-gray-500 ${config.text} ${className}`}>
        {showIcon && <Coins className={config.icon} />}
        <span>Loading...</span>
      </div>
    );
  }

  if (credits === null) {
    return (
      <div className={`flex items-center space-x-2 text-gray-500 ${config.text} ${className}`}>
        {showIcon && <Coins className={config.icon} />}
        <span>-- credits</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div
        className={`flex items-center space-x-2 ${
          isLowCredit ? 'text-red-600' : 'text-gray-900'
        } ${config.text}`}
      >
        {showIcon && (
          <Coins className={`${config.icon} ${isLowCredit ? 'text-red-500' : 'text-yellow-500'}`} />
        )}
        <span className="font-medium">{CreditService.formatCredits(credits)}</span>
      </div>

      {isLowCredit && (
        <div title="Low credit balance">
          <AlertTriangle className="w-4 h-4 text-red-500" />
        </div>
      )}

      {showRefreshButton && onRefresh && (
        <button
          onClick={handleRefresh}
          disabled={loading}
          className={`p-1 hover:bg-gray-100 rounded transition-colors ${
            loading ? 'animate-spin' : ''
          }`}
          title="Refresh credits"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
